import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router-dom";

import { AIChatSidebar } from "@/modules/yetakchi-ai/ui/AIChatSidebar";

import aiBgGraphic from "@/assets/icons/ai-bg-graphics.svg";
import logo from "@/assets/yia_logo.svg";
import { useAuthContext } from "@/auth/hooks/useAuthContext";
import { usePermissions } from "@/auth/hooks/usePermissions";
// import { Avatar, AvatarFallback, AvatarImage } from "@/components/Avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/Collapsible/Collapsible";
import { ContentSkeleton } from "@/components/PageLoading/ContentSkeleton";
import {
  Sidebar,
  SidebarContent,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenu,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarHeader,
  SidebarProvider,
  SidebarFooter,
} from "@/components/Sidebar";
import { SiteHeader } from "@/components/Sidebar/SiteHeader";
import { AccessLevel } from "@/constants/accessLevel";
import { usePathname } from "@/hooks/usePathname";
import {
  getMainRoutes,
  getSidebarItems,
  isRouteActive,
} from "@/routes/config/routes";

const PageLoadingFallback = () => {
  return <ContentSkeleton />;
};

const YOUTH_LIST_PATH = "/youth-list";

export const MainLayout = () => {
  const location = useLocation();
  const pathname = usePathname();
  const { t } = useTranslation("layout");
  const { user } = useAuthContext();
  const { permissions, accountType } = usePermissions();
  const accessLevel = user?.role?.access_level as AccessLevel | undefined;
  const prevLocationRef = useRef(`${location.pathname}${location.search}`);
  // Store referrer (path + queries) when user navigates TO youth list so "back from page 1" goes there
  useEffect(() => {
    const current = `${location.pathname}${location.search}`;
    const isListPage = location.pathname === YOUTH_LIST_PATH;
    const prev = prevLocationRef.current;
    const prevWasList = prev.startsWith(YOUTH_LIST_PATH);
    if (isListPage && !prevWasList) {
      sessionStorage.setItem("youthListExitTarget", prev);
    }
    prevLocationRef.current = current;
  }, [location.pathname, location.search]);

  const CAREER_BADGE_KEY = "careerEditBadge";
  const [careerBadgeColor, setCareerBadgeColor] = useState<"red" | "green">(
    () =>
      localStorage.getItem(CAREER_BADGE_KEY) === "green" ? "green" : "red",
  );

  // Listen for localStorage changes from the career-edit page
  const handleStorageChange = useCallback((e: StorageEvent) => {
    if (e.key === CAREER_BADGE_KEY) {
      setCareerBadgeColor(e.newValue === "green" ? "green" : "red");
    }
  }, []);

  useEffect(() => {
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [handleStorageChange]);

  // Also check on route change (same-tab localStorage writes don't fire StorageEvent)
  useEffect(() => {
    const stored = localStorage.getItem(CAREER_BADGE_KEY);
    setCareerBadgeColor(stored === "green" ? "green" : "red");
  }, [pathname]);

  const mainRoutes = useMemo(() => getMainRoutes(t), [t]);
  const sidebarItems = useMemo(
    () => getSidebarItems(t, permissions, accountType, accessLevel),
    [t, permissions, accountType, accessLevel],
  );

  // Find current route to check if sidebar should be shown (including nested routes)
  const currentRoute = mainRoutes.find(
    (route) => pathname === route.path || pathname.startsWith(`${route.path}/`),
  );
  const showSidebar = currentRoute?.showSidebar ?? true;
  const isAIPage = pathname.startsWith("/yetakchi-ai");

  const leaderAI = mainRoutes.find((route) => route.path === "/yetakchi-ai");
  const LeaderAIIcon = leaderAI ? leaderAI.icon : null;
  const leaderAIDescription = leaderAI ? leaderAI.description : "";
  const leaderAILabel = leaderAI ? leaderAI.label : "";
  const showLeaderAI =
    accountType === "SUPER_ADMIN" || accessLevel === "republic";
  return (
    <div>
      <SidebarProvider>
        <div className="flex h-dvh w-full overflow-hidden">
          {showSidebar &&
            (isAIPage ? (
              <Sidebar className="bg-secondary-solid">
                <AIChatSidebar />
              </Sidebar>
            ) : (
              <Sidebar className="bg-secondary-solid">
                <SidebarHeader>
                  <div className="flex px-[20px] py-[10px]">
                    <Link to="/dashboard">
                      <img src={logo} alt="logo" width={180} height={43} />
                    </Link>
                  </div>
                </SidebarHeader>

                <SidebarContent className="p-[20px]">
                  <SidebarMenu>
                    {sidebarItems.map((item) => {
                      const isActive = isRouteActive(pathname, item.path);
                      const Icon =
                        isActive && item.iconActive
                          ? item.iconActive
                          : item.icon;
                      const hasChildren =
                        item.children &&
                        item.children.some((child) => child.showSidebar);
                      const firstChildPath =
                        hasChildren && item.children
                          ? item.children.find((c) => c.showSidebar)?.path
                          : undefined;
                      const linkPath = firstChildPath
                        ? `${item.path}/${firstChildPath}`
                        : item.path;

                      const isSettingsItem = item.path === "/settings";

                      return (
                        <Collapsible
                          key={item.path}
                          asChild
                          defaultOpen={isActive}
                          className="group/collapsible"
                        >
                          <SidebarMenuItem hasBorderTop={isSettingsItem}>
                            {hasChildren ? (
                              <CollapsibleTrigger asChild>
                                <SidebarMenuButton isActive={isActive}>
                                  <Icon />
                                  <p>{item.label}</p>
                                </SidebarMenuButton>
                              </CollapsibleTrigger>
                            ) : (
                              <SidebarMenuButton asChild isActive={isActive}>
                                <Link to={linkPath}>
                                  <Icon />
                                  <p>{item.label}</p>
                                </Link>
                              </SidebarMenuButton>
                            )}
                            {item.path === "/career-edit" && (
                              <SidebarMenuBadge className="!right-3 !top-1/2 !min-w-0 -translate-y-1/2 !px-0">
                                <span
                                  className={`block h-2.5 w-2.5 rounded-full ${careerBadgeColor === "green" ? "bg-success-500" : "bg-error-500"}`}
                                />
                              </SidebarMenuBadge>
                            )}
                            {hasChildren && (
                              <CollapsibleContent>
                                <SidebarMenuSub>
                                  {item.children
                                    ?.filter((child) => child.showSidebar)
                                    .map((child) => {
                                      const childPath = `${item.path}/${child.path}`;
                                      const isChildActive = isRouteActive(
                                        pathname,
                                        childPath,
                                      );
                                      return (
                                        <SidebarMenuSubItem key={childPath}>
                                          <SidebarMenuSubButton
                                            asChild
                                            isActive={isChildActive}
                                          >
                                            <Link to={childPath}>
                                              <span>{child.label}</span>
                                            </Link>
                                          </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                      );
                                    })}
                                </SidebarMenuSub>
                              </CollapsibleContent>
                            )}
                          </SidebarMenuItem>
                        </Collapsible>
                      );
                    })}
                  </SidebarMenu>
                </SidebarContent>
                {showLeaderAI && (
                  <SidebarFooter className="pb-[20px]">
                    <SidebarMenuButton
                      asChild
                      className="!border !border-primary-400 !bg-gradient-to-b !from-primary-400 !to-primary-500 !font-semibold !text-white hover:!from-primary-500 hover:!to-primary-600 data-[active=true]:!border-primary-400 data-[active=true]:!bg-gradient-to-b data-[active=true]:!from-primary-400 data-[active=true]:!to-primary-500 data-[active=true]:!font-semibold data-[active=true]:!text-white [&_path]:!stroke-white [&_svg]:!text-white"
                    >
                      <Link
                        to="/yetakchi-ai"
                        className="relative flex w-full flex-col !items-start gap-2 !p-3"
                      >
                        <div className="items-left flex gap-2">
                          {LeaderAIIcon && <LeaderAIIcon className="h-5 w-5" />}
                          <p className="text-sm font-semibold">
                            {leaderAILabel}
                          </p>
                        </div>
                        <p className="w-[160px] text-xs text-white text-opacity-70">
                          {leaderAIDescription}
                        </p>
                        <img
                          src={aiBgGraphic}
                          alt="ai-bg-graphic"
                          className="absolute right-0 top-0 object-contain"
                        />
                      </Link>
                    </SidebarMenuButton>
                  </SidebarFooter>
                )}
              </Sidebar>
            ))}
          <main className="flex h-dvh min-w-0 flex-1 flex-col overflow-hidden">
            <div className="flex-shrink-0">
              <SiteHeader />
            </div>
            <div className="min-w-0 flex-1 overflow-y-auto">
              <div className="p-4 sm:p-5 lg:p-6">
                <Suspense fallback={<PageLoadingFallback />}>
                  <Outlet />
                </Suspense>
              </div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    </div>
  );
};
